import React, { FC, useEffect, useState } from "react";
import { Close } from "@mui/icons-material";
import {
  Dialog,
  Typography,
  Link,
  Divider,
  Switch,
  Button,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useLocalStorage } from "usehooks-ts";
import {
  ALLOW_ANALYTICS_KEY,
  ALLOW_STRICTLY_NECESSARY_KEY,
} from "../constants";

type CookieSettingsDialogProps = {
  open: boolean;
  onClose: () => void;
};

const CookieSettingsDialog: FC<CookieSettingsDialogProps> = ({
  open,
  onClose,
}) => {
  const [allowNecessaryInStorage, setAllowNecessaryInStorage] = useLocalStorage<
    boolean | undefined
  >(ALLOW_STRICTLY_NECESSARY_KEY, true);
  const [allowNecessary, setAllowNecessary] = useState(allowNecessaryInStorage);
  const [allowAnalyticsCookiesInStorage, setAllowAnalyticsCookiesInStorage] =
    useLocalStorage<boolean | undefined>(ALLOW_ANALYTICS_KEY, true);
  const [allowAnalytics, setAllowAnalytics] = useState(
    allowAnalyticsCookiesInStorage
  );

  useEffect(() => {
    setAllowNecessary(allowNecessaryInStorage);
    setAllowAnalytics(allowAnalyticsCookiesInStorage);
  }, [allowNecessaryInStorage, allowAnalyticsCookiesInStorage]);

  const handleSave = () => {
    setAllowNecessaryInStorage(allowNecessary);
    setAllowAnalyticsCookiesInStorage(allowAnalytics);
    onClose();
  };

  const handleAcceptAll = () => {
    setAllowNecessaryInStorage(true);
    setAllowAnalyticsCookiesInStorage(true);
    onClose();
  };

  return (
    <Dialog
      sx={{
        "& .MuiDialog-paper": {
          backgroundColor: "white",
          px: { xs: 1.5, sm: 4 },
          pt: 8,
          pb: { xs: 3, sm: 9 },
        },
        "& .MuiPaper-root": {
          boxShadow: "none",
          width: { xs: "100vw", sm: "490px", md: "600px" },
          margin: 0,
          borderRadius: "8px",
          borderBottomLeftRadius: { xs: "0", sm: "8px" },
          borderBottomRightRadius: { xs: "0", sm: "8px" },
        },
        "& .MuiDialog-container": {
          alignItems: { xs: "flex-end", sm: "center" },
        },
      }}
      open={open}
      onClose={onClose}
    >
      <Box
        display="inline"
        position="absolute"
        sx={{
          top: { xs: "12px", sm: "24px" },
          right: { xs: "12px", sm: "24px" },
        }}
      >
        <Close
          sx={{
            cursor: "pointer",
          }}
          color="action"
          onClick={onClose}
        />
      </Box>
      <Typography pt={0} mb={2} variant="h5">
        Customize your cookie settings
      </Typography>
      <Typography variant="body1" mb={4} color="text.secondary">
        We use cookies to improve your browsing experience. You can configure
        your cookies settings here. Find out more about our cookie policy at{" "}
        <Link underline="always" href="/privacy-policy">
          mettle-studio.com/privacy-policy/
        </Link>
      </Typography>
      <Divider />
      <Box>
        <Stack direction="row" alignItems="center" mt={3}>
          <Switch
            checked={allowNecessary}
            onChange={(e) => setAllowNecessary(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <Typography variant="body1" ml={1}>
            Strictly necessary cookies
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" my={3}>
          <Switch
            checked={allowAnalytics}
            onChange={(e) => setAllowAnalytics(e.target.checked)}
            onClick={(e) => e.stopPropagation()}
            size="small"
          />
          <Typography variant="body1" ml={1}>
            Analytics Cookies
          </Typography>
        </Stack>
      </Box>
      <Divider />
      <Button
        onClick={handleSave}
        variant="contained"
        sx={{
          mt: 4,
          textTransform: "none",
        }}
      >
        Save settings
      </Button>
      <Button
        sx={{
          mt: 1,
          textTransform: "none",
        }}
        variant="outlined"
        onClick={handleAcceptAll}
      >
        Accept all cookies
      </Button>
    </Dialog>
  );
};

export default CookieSettingsDialog;
